import { useQuery, useQueryClient, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import API from './utils/api';
import { 
    CreateEmployeeOvertimeEntryRequestDto,
    UpdateEmployeeOvertimeEntryRequestDto,
    QueryEmployeeOvertimeEntryRequestDto,
    EmployeeOvertimeEntryRequestPaginator,
    EmployeeOvertimeEntryRequestDto,
    EmployeeOvertimeEntryInputDto
} from '../models/employee-overtime-entry-request.dto';
import { RevokeTimeRequestDto, TimeRequestStatus } from '../models/time-request.dto';
import ENDPOINTS from './utils/endpoints';

const { GET, DETAIL } = ENDPOINTS.HR.EMPLOYEE_OVERTIME_ENTRY_REQUEST;

// Lists and the open request both change when a request does
function useInvalidateRequests() {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries(GET);
        queryClient.invalidateQueries(DETAIL);
    };
}

export function useGetEmployeeOvertimeEntryRequest(
    options?: QueryEmployeeOvertimeEntryRequestDto,
    queryOptions?: { staleTime?: number, refetchOnWindowFocus?: boolean },
) {
    const request = (options: QueryEmployeeOvertimeEntryRequestDto) => API.HrAPI.getEmpOvertimeEntryRequests(options);

    const { data, isLoading, error, refetch } = useQuery<EmployeeOvertimeEntryRequestPaginator, Error>(
        [GET, options],
        ({ queryKey, pageParam }) => request(Object.assign({}, queryKey[1], pageParam)),
        { ...API.Handlers.pageParamOption, ...queryOptions },
    );

    return { data, isLoading, error, refetch };
}

export function useGetEmployeeOvertimeEntryRequestById(id?: number) {
    const { data, isLoading, error } = useQuery<{ data: EmployeeOvertimeEntryRequestDto }, Error>(
        [DETAIL, id],
        () => API.HrAPI.getEmpOvertimeEntryRequestById(id!),
        { enabled: !!id },
    );
    
    return { data, isLoading, error };
}

export function useCreateEmployeeOvertimeEntryRequest(onSuccessCallback?: (responsData?: any) => void) {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ payload }: { payload: CreateEmployeeOvertimeEntryRequestDto }) => API.HrAPI.createEmpOvertimeEntryRequest(payload),
        {
            onSuccess: (response) => {
                toast.success('Overtime request submitted for approval');
                if (onSuccessCallback) {
                    onSuccessCallback(response);
                };
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: invalidate,
        },
    );
}

export function useUpdateEmployeeOvertimeEntryRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id, payload }: { id: number, payload: UpdateEmployeeOvertimeEntryRequestDto }) => API.HrAPI.updateEmpOvertimeEntryRequest(id, payload),
        {
            onSuccess: () => {
                toast.success('Overtime request updated successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: invalidate,
        },
    );
}

export function useDeleteEmployeeOvertimeEntryRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id }: { id: number }) => API.HrAPI.deleteEmpOvertimeEntryRequest(id),
        {
            onSuccess: () => {
                toast.success('Overtime request deleted successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: invalidate,
        },
    );
}

export function useAddEmployeeOvertimeEntryResponse() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id, payload }: { id: number, payload: EmployeeOvertimeEntryInputDto }) => API.HrAPI.addEmpOvertimeEntryResponse(id, payload),
        {
            onSuccess: ({ data }) => {
                toast.success('Response submitted successfully');
                // Overtime approved after payroll ran is paid in a later pay period
                if (data.status === TimeRequestStatus.APPROVED) {
                    const entry = data.employeeOvertimeEntry;
                    if (!entry) {
                        toast.info('This overtime will be paid once the next pay period opens');
                    } else if (entry.payPeriodId !== data.payPeriodId) {
                        toast.info(
                            `Payroll had already run for ${data.payPeriod?.code ?? 'its pay period'}; `
                            + `this overtime will be paid in ${entry.payPeriod?.code ?? 'the next pay period'}`
                        );
                    }
                }
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        },
    );
}

export function useCancelEmployeeOvertimeEntryRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id }: { id: number }) => API.HrAPI.cancelEmpOvertimeEntryRequest(id),
        {
            onSuccess: () => {
                toast.success('Overtime request cancelled successfully');
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        },
    );
}

export function useRevokeEmployeeOvertimeEntryRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id, payload }: { id: number, payload: RevokeTimeRequestDto }) => API.HrAPI.revokeEmpOvertimeEntryRequest(id, payload),
        {
            onSuccess: ({ data }) => {
                toast.success('Overtime request revoked successfully');
                if (data.payrollRerunRequired) {
                    toast.warning('Payroll had already been run with this overtime; run it again to remove it');
                }
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        },
    );
}
